import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRecordVinyl } from '@fortawesome/pro-light-svg-icons/faRecordVinyl';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { TypedMemo } from '../../common/types';
import { Panel, PanelHead } from '../../music24/components/primitives/Panel';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { CatalogFilterValue, CatalogItem } from '../../../redux/api/fymAPI.types';
import { ErrorAPIResult } from '../../../redux/api/utils/isSuccessfulResult';
import { CatalogFilterChips } from './CatalogFilterChips';
import { CatalogTableRow } from './CatalogTableRow';
import { CatalogRowMenuActions } from './CatalogRowMenu';
import { catalogSongMessages } from './song/catalogSongMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CatalogTablePanelProps {
  accountId: number;
  items?: CatalogItem[];
  isLoading: boolean;
  error?: ErrorAPIResult['error'];
  retry: () => void;
  search: string;
  onSearchChange: (search: string) => void;
  filter: CatalogFilterValue;
  onFilterChange: (filter: CatalogFilterValue) => void;
  onOpenSmartLink: (smartLinkId: number) => void;
  onCreateLink: (item: CatalogItem) => void;
  onOpenSong: CatalogRowMenuActions['onOpenSong'];
  onMakeInStudio: (item: CatalogItem) => void;
  onOpenRelease: (item: CatalogItem) => void;
  onCopyLink: (item: CatalogItem) => void;
  onRemove: (item: CatalogItem) => void;
  /** Undefined: the rows show no "Merge with another song…" (design catalog-merge a1). */
  onMerge?: (item: CatalogItem) => void;
}

/** Everything Music24 will let this account promote. */
const CatalogTablePanelComponent: React.FC<CatalogTablePanelProps> = ({
  accountId,
  items,
  isLoading,
  error,
  retry,
  search,
  onSearchChange,
  filter,
  onFilterChange,
  onOpenSmartLink,
  onCreateLink,
  onOpenSong,
  onMakeInStudio,
  onOpenRelease,
  onCopyLink,
  onRemove,
  onMerge,
}) => {
  const intl = useIntl();

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onSearchChange(event.target.value),
    [onSearchChange]
  );

  const isNarrowed = search !== '' || filter !== CatalogFilterValue.all;

  return (
    <Panel>
      <PanelHead
        icon={<FontAwesomeIcon icon={faRecordVinyl} />}
        title={intl.formatMessage({ id: 'app.sml.catalog.table.title', defaultMessage: 'Your catalog' })}
        right={
          items === undefined
            ? undefined
            : intl.formatMessage(
                {
                  id: 'app.sml.catalog.table.count',
                  defaultMessage: '{count, plural, one {# release} other {# releases}}',
                },
                { count: items.length }
              )
        }
      />
      <div className={styles.tableBar}>
        <div className={styles.search}>
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <input
            type="search"
            className={styles.searchInput}
            value={search}
            onChange={handleSearch}
            placeholder={intl.formatMessage({
              id: 'app.sml.catalog.table.search',
              defaultMessage: 'Search your catalog',
            })}
            aria-label={intl.formatMessage({
              id: 'app.sml.catalog.table.search',
              defaultMessage: 'Search your catalog',
            })}
          />
        </div>
        <CatalogFilterChips value={filter} onChange={onFilterChange} />
      </div>
      {error !== undefined && <ErrorView error={error} retry={retry} />}
      {error === undefined && (isLoading || items === undefined) && <LoadingView />}
      {error === undefined && items !== undefined && items.length === 0 && (
        <p className={styles.emptyState}>
          {isNarrowed
            ? intl.formatMessage({
                id: 'app.sml.catalog.table.empty_filtered',
                defaultMessage: 'Nothing here matches. Widen the filter or clear the search.',
              })
            : intl.formatMessage({
                id: 'app.sml.catalog.table.empty',
                defaultMessage:
                  'Nothing claimed yet. Claim a release you already put out, or add a track that is not out anywhere.',
              })}
        </p>
      )}
      {error === undefined && items !== undefined && items.length > 0 && (
        <>
          <div className={styles.tableHead}>
            <span>{intl.formatMessage({ id: 'app.sml.catalog.table.release', defaultMessage: 'Release' })}</span>
            <span>{intl.formatMessage({ id: 'app.sml.catalog.table.ownership', defaultMessage: 'Ownership' })}</span>
            <span>{intl.formatMessage({ id: 'app.sml.catalog.table.date', defaultMessage: 'Date' })}</span>
            {/* design f1: "On services" moved into the song files sheet to make room for the Song column. */}
            <span className={styles.tableHeadNew}>{intl.formatMessage(catalogSongMessages.column)}</span>
            <span>{intl.formatMessage({ id: 'app.sml.catalog.table.smartlink', defaultMessage: 'Smartlink' })}</span>
            <span />
          </div>
          {items.map((item) => (
            <CatalogTableRow
              key={item.uuid}
              accountId={accountId}
              item={item}
              onOpenSmartLink={onOpenSmartLink}
              onCreateLink={onCreateLink}
              onOpenSong={onOpenSong}
              onMakeInStudio={onMakeInStudio}
              onOpenRelease={onOpenRelease}
              onCopyLink={onCopyLink}
              onRemove={onRemove}
              onMerge={onMerge}
            />
          ))}
        </>
      )}
    </Panel>
  );
};

export const CatalogTablePanel = TypedMemo(CatalogTablePanelComponent);
